import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popper,
  Collapse,
  Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import ContentCopy from '@mui/icons-material/ContentCopy';
import HelpOutline from '@mui/icons-material/HelpOutline';
import AccessTime from '@mui/icons-material/AccessTime';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import BuildCircle from '@mui/icons-material/BuildCircle';
import ChevronRight from '@mui/icons-material/ChevronRight';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import SingleSelect from '../../../shared/ui/SingleSelect';
import AIFileLink from '../../../shared/ui/AIFileLink/AIFileLink';
import { showNotification } from '../../../shared/snackbar/redux/snackbar.slice';

const StyledMessageContainer = styled(Box)(({ theme, sender }) => ({
  display: 'flex',
  justifyContent: sender === 'user' ? 'flex-end' : 'flex-start',
  marginBottom: theme.spacing(1),
  animation: 'fadeIn 0.3s ease-in',
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
}));

const StyledMessageBubble = styled(Paper)(({ theme, sender, isError, pinned }) => ({
  padding: theme.spacing(2),
  maxWidth: '70%',
  backgroundColor: isError
    ? '#ffebee'
    : pinned
      ? sender === 'user'
        ? '#e3f2fd'
        : '#e8f5e9'
      : sender === 'user'
        ? '#e3f2fd'
        : '#f1f8e9',
  borderRadius: theme.spacing(2),
  boxShadow: pinned
    ? '0 3px 12px rgba(0,0,0,0.15), inset 0 0 0 2px #ffb300'
    : '0 2px 8px rgba(0,0,0,0.1)',
  position: 'relative',
  border: isError ? '1px solid #f44336' : pinned ? '1px solid #ffb300' : 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: pinned
      ? '0 5px 16px rgba(0,0,0,0.2), inset 0 0 0 2px #ffb300'
      : '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const StyledTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  opacity: 0.8,
}));

const StyledSenderLabel = styled(Typography)(({ theme, sender }) => ({
  fontSize: '0.75rem',
  fontWeight: 'bold',
  color: sender === 'user' ? theme.palette.primary.main : theme.palette.success.main,
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

function parseMCPSegments(content) {
  const segments = [];
  const regex = /\[\[mcp_call:([^:]+):([^:]+):([\s\S]*?)\]\]/g;
  let lastIndex = 0;
  let match;
  do {
    match = regex.exec(content);
    if (match) {
      const [, server, tool, rawParams] = match;
      if (match.index > lastIndex) {
        segments.push({ type: 'text', content: content.slice(lastIndex, match.index) });
      }
      let params;
      try {
        params = JSON.parse(rawParams);
      } catch {
        params = rawParams;
      }
      segments.push({ type: 'mcp_call', server, tool, params });
      lastIndex = match.index + match[0].length;
    }
  } while (match);
  if (lastIndex < content.length) {
    segments.push({ type: 'text', content: content.slice(lastIndex) });
  }
  return segments.length > 0 ? segments : [{ type: 'text', content }];
}

function MCPCallInline({ server, tool, params, getToolTitle }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box sx={{ my: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          bgcolor: 'action.hover',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.selected' },
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <IconButton size="small" sx={{ p: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
          <ChevronRight fontSize="small" />
        </IconButton>
        <BuildCircle fontSize="small" color="action" />
        <Typography variant="body2" fontWeight="medium">
          {getToolTitle(server, tool)}
        </Typography>
        <Chip label={server} size="small" variant="outlined" sx={{ ml: 'auto' }} />
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Параметры вызова:
          </Typography>
          <pre
            style={{
              fontSize: '0.8rem',
              background: '#f5f5f5',
              padding: '0.5rem',
              borderRadius: 4,
              overflow: 'auto',
              margin: 0,
            }}
          >
            {typeof params === 'object' ? JSON.stringify(params, null, 2) : params}
          </pre>
        </Box>
      </Collapse>
    </Box>
  );
}

function CompanyAIPanelEnhanced({ aiChatContext }) {
  const dispatch = useDispatch();
  const [inputMessage, setInputMessage] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [processedMCPCalls, setProcessedMCPCalls] = useState(new Set());
  const suggestionsRef = useRef(null);
  const suggestionsHoverRef = useRef(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const openSuggestions = () => {
    suggestionsHoverRef.current = true;
    setSuggestionsOpen(true);
  };

  const closeSuggestions = () => {
    suggestionsHoverRef.current = false;
    setTimeout(() => {
      if (!suggestionsHoverRef.current) {
        setSuggestionsOpen(false);
      }
    }, 200);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(showNotification({ message: 'Скопировано', severity: 'success' }));
    } catch {
      dispatch(showNotification({ message: 'Ошибка копирования', severity: 'error' }));
    }
  };

  const {
    messages = [],
    isLoading,
    isSending,
    roles = [],
    selectedRoleId,
    handleRoleChange,
    sessions = [],
    activeSessionId,
    handleCreateSession,
    handleSelectSession,
    handleDeleteSession,
    handleSendMessage,
    suggestedQuestions = [],
    pendingMCPCalls = [],
    currentMCPMessageId,
    handleApproveMCPCall,
    handleRejectMCPCall,
    mcpToolsMap = {},
    handleTogglePin,
    pinnedOnly,
    setPinnedOnly,
  } = aiChatContext || {};

  useEffect(() => {
    if (activeSessionId) {
      inputRef.current?.focus();
    }
  }, [activeSessionId]);

  const getToolTitle = (server, tool) => mcpToolsMap[`${server}:${tool}`] || tool;

  const filteredMessages = pinnedOnly ? messages.filter(m => m.is_pinned) : messages;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setProcessedMCPCalls(new Set());
  }, [currentMCPMessageId]);

  const handleSend = async () => {
    const text = inputMessage.trim();
    if (!text || !handleSendMessage) return;
    await handleSendMessage(text);
    setInputMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInputMessage(question);
  };

  const confirmDeleteSession = async () => {
    if (sessionToDelete) {
      await handleDeleteSession?.(sessionToDelete);
    }
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderSessionSidebar = () => (
    <Paper
      elevation={0}
      sx={{
        width: 300,
        flexShrink: 0,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon fontSize="small" />
            Сессии
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCreateSession?.()}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Новая
          </Button>
        </Box>
      </Box>
      {sessions.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Нет сессий. Создайте новую.
          </Typography>
        </Box>
      ) : (
        <List sx={{ overflow: 'auto', flex: 1 }} disablePadding>
          {sessions.map((session, index) => (
            <React.Fragment key={session.id}>
              {index > 0 && <Divider component="li" />}
              <ListItemButton
                selected={session.id === activeSessionId}
                onClick={() => handleSelectSession?.(session.id)}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SmartToyIcon fontSize="small" color={session.id === activeSessionId ? 'primary' : 'action'} />
                </ListItemIcon>
                <Tooltip title={session.title} arrow placement="top">
                  <ListItemText
                    primary={session.title}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{formatDate(session.created_at)}</span>
                        {session.pinned_count > 0 && (
                          <>
                            <PushPin fontSize="inherit" sx={{ fontSize: '0.75rem', opacity: 0.6 }} />
                            <span>{session.pinned_count}</span>
                          </>
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: session.id === activeSessionId ? 700 : 500,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </Tooltip>
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSessionToDelete(session.id);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ ml: 1, opacity: 0.4, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );

  const sessionWithPinned = sessionToDelete
    ? sessions.find(s => s.id === sessionToDelete)
    : null;
  const hasPinnedMessages = sessionWithPinned?.pinned_count > 0;

  return (
    <Box sx={{
      height: 'calc(100vh - 200px)',
      display: 'flex',
      flexDirection: 'row',
      gap: 3,
      maxWidth: '1520px',
      margin: '0 auto',
    }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 2 }}>
          <Tooltip title={pinnedOnly ? 'Показать все сообщения' : 'Показать только закрепленные'} arrow>
            <IconButton
              color={pinnedOnly ? 'primary' : 'default'}
              onClick={() => setPinnedOnly?.(!pinnedOnly)}
            >
              <PushPin />
            </IconButton>
          </Tooltip>
          {roles.length > 0 && (
            <SingleSelect
              name="role"
              label="Роль"
              value={selectedRoleId || ''}
              items={roles.map(r => ({ value: r.id, name: r.name }))}
              onChange={(e) => handleRoleChange?.(e.target.value)}
            />
          )}
          {suggestedQuestions.length > 0 && (
            <>
              <IconButton
                ref={suggestionsRef}
                onMouseEnter={openSuggestions}
                onMouseLeave={closeSuggestions}
                size="large"
              >
                <HelpOutline />
              </IconButton>
              <Popper
                open={suggestionsOpen}
                anchorEl={suggestionsRef.current}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{ borderRadius: 2, p: 2, maxWidth: 400 }}
                  onMouseEnter={openSuggestions}
                  onMouseLeave={closeSuggestions}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Попробуйте спросить:
                  </Typography>
                  <Grid container spacing={1}>
                    {suggestedQuestions.map((question) => (
                      <Grid item key={question}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleSuggestedQuestion(question);
                            setSuggestionsOpen(false);
                          }}
                          sx={{ borderRadius: 2, textTransform: 'none' }}
                          startIcon={<SmartToyIcon fontSize="small" />}
                        >
                          {question}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Popper>
            </>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            mb: 3,
            p: 3,
            overflow: 'auto',
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            minHeight: '400px',
          }}
        >
          {!activeSessionId && !isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                Создайте новую сессию или просто задай вопрос, чтобы начать общение
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ width: '100%' }}>
              {filteredMessages.map((message) => (
                <StyledMessageContainer key={message.id} sender={message.role}>
                  <StyledMessageBubble sender={message.role} isError={message.status === 'ERROR'} pinned={message.is_pinned}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {message.role === 'user' ? (
                        <PersonIcon fontSize="small" color="primary" />
                      ) : (
                        <SmartToyIcon fontSize="small" color="success" />
                      )}
                      <StyledSenderLabel sender={message.role}>
                        {message.role === 'user' ? 'Вы' : 'ИИ-помощник'}
                      </StyledSenderLabel>
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={message.is_pinned ? 'Открепить' : 'Закрепить'} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleTogglePin?.(message.id, !message.is_pinned)}
                            sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                          >
                            {message.is_pinned ? (
                              <PushPin fontSize="small" color="warning" />
                            ) : (
                              <PushPinOutlined fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {message.role === 'assistant' && (
                          <Tooltip title="Копировать" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(message.content)}
                              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    {message.role === 'assistant' ? (
                      <Box
                        sx={{
                          '& pre': {
                            borderRadius: 1,
                            overflow: 'auto',
                            p: 1.5,
                            bgcolor: '#f6f8fa',
                          },
                          '& code': {
                            fontSize: '0.875rem',
                          },
                          '& p': {
                            margin: 0,
                            fontSize: '1rem',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                          },
                          '& ul, & ol': {
                            margin: 0,
                            pl: 2.5,
                            fontSize: '1rem',
                            lineHeight: 1.6,
                          },
                          '& li': {
                            fontSize: '1rem',
                            lineHeight: 1.6,
                          },
                          '& table': {
                            borderCollapse: 'collapse',
                            width: '100%',
                            mb: 1,
                          },
                          '& th, & td': {
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 1,
                            fontSize: '0.875rem',
                          },
                          '& a': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        {(() => {
                          const segments = parseMCPSegments(message.content);
                          if (segments.length === 1 && segments[0].type === 'text') {
                            return (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeHighlight]}
                                components={{ a: AIFileLink }}
                              >
                                {segments[0].content}
                              </ReactMarkdown>
                            );
                          }
                          return segments.map((seg) => {
                            const segKey = segments.indexOf(seg);
                            if (seg.type === 'text') {
                              return (
                                <ReactMarkdown
                                  key={`seg-${segKey}`}
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeHighlight]}
                                  components={{ a: AIFileLink }}
                                >
                                  {seg.content}
                                </ReactMarkdown>
                              );
                            }
                            return <MCPCallInline key={`mcp-${segKey}`} server={seg.server} tool={seg.tool} params={seg.params} getToolTitle={getToolTitle} />;
                          });
                        })()}
                        {message.id === currentMCPMessageId && pendingMCPCalls.length > 0 && (
                          <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <BuildCircle fontSize="inherit" />
                              Требуется подтверждение вызова инструментов
                            </Typography>
                            {pendingMCPCalls.map((call) => {
                              const isProcessed = processedMCPCalls.has(call.id);
                              return (
                                <Paper
                                  key={call.id}
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    mb: 1,
                                    opacity: isProcessed ? 0.45 : 1,
                                    transition: 'opacity 0.3s',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip
                                      label={call.server_name}
                                      size="small"
                                      color={isProcessed ? 'default' : 'primary'}
                                      variant="outlined"
                                    />
                                    <Typography variant="body2" fontWeight="bold">
                                      {getToolTitle(call.server_name, call.tool_name)}
                                    </Typography>
                                  </Box>
                                  <pre
                                    style={{
                                      fontSize: '0.8rem',
                                      background: '#f5f5f5',
                                      padding: '0.5rem',
                                      borderRadius: 4,
                                      overflow: 'auto',
                                      margin: '0.5rem 0',
                                    }}
                                  >
                                    {JSON.stringify(call.arguments, null, 2)}
                                  </pre>
                                  {!isProcessed && (
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleOutline />}
                                        onClick={async () => {
                                          await handleApproveMCPCall?.(call.id);
                                          setProcessedMCPCalls(prev => new Set(prev).add(call.id));
                                        }}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                      >
                                        Одобрить
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelOutlined />}
                                        onClick={async () => {
                                          await handleRejectMCPCall?.(call.id);
                                          setProcessedMCPCalls(prev => new Set(prev).add(call.id));
                                        }}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                      >
                                        Отклонить
                                      </Button>
                                    </Box>
                                  )}
                                </Paper>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <ListItemText
                        primary={message.content}
                        primaryTypographyProps={{
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                        }}
                      />
                    )}
                    <StyledTimestamp>
                      {formatDate(message.created_at)}
                      {(message.status === 'PENDING' || message.status === 'PROCESSING') && (
                        <AccessTime fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', opacity: 0.6 }} />
                      )}
                      {message.status === 'ERROR' && (
                        <ErrorOutline fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', color: 'error.main' }} />
                      )}
                    </StyledTimestamp>
                    {message.error_text && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {message.error_text}
                      </Typography>
                    )}
                  </StyledMessageBubble>
                </StyledMessageContainer>
              ))}

              {isSending && (
                <StyledMessageContainer sender="assistant">
                  <StyledMessageBubble sender="assistant">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SmartToyIcon fontSize="small" color="success" />
                      <Box>
                        <Typography variant="body1">ИИ анализирует запрос...</Typography>
                        <CircularProgress size={20} sx={{ mt: 1 }} />
                      </Box>
                    </Box>
                  </StyledMessageBubble>
                </StyledMessageContainer>
              )}

              <div ref={messagesEndRef} />
            </List>
          )}
        </Paper>



        <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            <Box sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                inputRef={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Введите ваш вопрос..."
                variant="outlined"
                disabled={isSending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!inputMessage.trim() || isSending}
                startIcon={isSending ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{
                  minWidth: '140px',
                  height: '56px',
                  borderRadius: 2,
                  fontWeight: 'bold',
                }}
              >
                {isSending ? 'Отправка...' : 'Отправить'}
              </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                ИИ-помощник анализирует данные вашей компании в режиме реального времени.
                Все сообщения обрабатываются с учетом контекста вашей деятельности.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {renderSessionSidebar()}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Удаление сессии</DialogTitle>
        <DialogContent>
          <Typography>
            {hasPinnedMessages
              ? 'В сессии есть закрепленные сообщения. Вы действительно хотите их удалить?'
              : 'Вы уверены, что хотите удалить эту сессию?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={confirmDeleteSession} variant="contained" color="error">Удалить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

MCPCallInline.propTypes = {
  server: PropTypes.string.isRequired,
  tool: PropTypes.string.isRequired,
  params: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  getToolTitle: PropTypes.func.isRequired,
}

CompanyAIPanelEnhanced.propTypes = {
  aiChatContext: PropTypes.shape({
    messages: PropTypes.arrayOf(PropTypes.shape({})),
    isLoading: PropTypes.bool,
    isSending: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.shape({})),
    selectedRoleId: PropTypes.string,
    handleRoleChange: PropTypes.func,
    sessions: PropTypes.arrayOf(PropTypes.shape({})),
    activeSessionId: PropTypes.string,
    handleCreateSession: PropTypes.func,
    handleSelectSession: PropTypes.func,
    handleDeleteSession: PropTypes.func,
    handleSendMessage: PropTypes.func,
    suggestedQuestions: PropTypes.arrayOf(PropTypes.string),
    pendingMCPCalls: PropTypes.arrayOf(PropTypes.shape({})),
    currentMCPMessageId: PropTypes.string,
    handleApproveMCPCall: PropTypes.func,
    handleRejectMCPCall: PropTypes.func,
    mcpToolsMap: PropTypes.objectOf(PropTypes.string),
    handleTogglePin: PropTypes.func,
    pinnedOnly: PropTypes.bool,
    setPinnedOnly: PropTypes.func,
  }),
};

CompanyAIPanelEnhanced.defaultProps = {
  aiChatContext: null,
};

export default CompanyAIPanelEnhanced;
